import {ref, watch, onUnmounted, computed} from 'vue';
import {navbarEventBus} from '@navbar/events';
import {useUserStore} from '@navbar-logged/features/user';
import {storeToRefs} from 'pinia';
import {setCssVariable, useDisplayMobile} from '@malt/utils';
import {NavBarWidth} from '../aside-menu.types';
import {useDisplayModeStore} from '../stores/display-mode.store';

export function useMobileNavBar() {
    const {isDisplayMobile} = useDisplayMobile();
    const displayModeStore = useDisplayModeStore();
    const userStore = useUserStore();
    const {selectedIdentity, hasSelectedClientIdentity} = storeToRefs(userStore);

    const isMobileNavBarOpened = ref(false);
    const serviceLevel = computed(() => selectedIdentity.value?.serviceLevel);

    let timeoutCssSetter: NodeJS.Timeout | null = null;

    function onDisplayMobileChange(state: boolean) {
        if (import.meta.client) {
            if (timeoutCssSetter) {
                clearTimeout(timeoutCssSetter);
            }
            if (state) {
                setCssVariable('--aside-menu-left', '-100%');
                setCssVariable('--layout-padding-left', '0px');
                timeoutCssSetter = setTimeout(() => {
                    setCssVariable('--aside-menu-width', `${NavBarWidth.Mobile}px`);
                }, 300);
            } else {
                isMobileNavBarOpened.value = false;
                document.body.style.overflow = '';
                displayModeStore.setNavbarDisplayMode({savePreference: false});
                setCssVariable('--aside-menu-left', '0px');
            }
        }
    }

    function setMobileNavBarOpened(value: boolean) {
        if (value) {
            isMobileNavBarOpened.value = true;
            setCssVariable('--aside-menu-left', '0px');
            document.body.style.overflow = 'hidden';
        } else {
            isMobileNavBarOpened.value = false;
            setCssVariable('--aside-menu-left', '-100%');
            document.body.style.overflow = '';
        }
    }

    function toggleMobileNavBarOpened() {
        setMobileNavBarOpened(!isMobileNavBarOpened.value);
    }

    watch(isDisplayMobile, onDisplayMobileChange, {immediate: true});

    watch(isMobileNavBarOpened, () => {
        if (hasSelectedClientIdentity.value && serviceLevel.value !== 'STANDARD') {
            navbarEventBus.emit('request:navbar-toggle-on-mobile', {isOpen: isMobileNavBarOpened.value});
        }
    });

    onUnmounted(() => {
        navbarEventBus.off('request:navbar-toggle-on-mobile');
        document.body.style.overflow = '';
    });

    return {
        toggleMobileNavBarOpened,
        setMobileNavBarOpened,
        isMobileNavBarOpened,
    };
}
